import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";

import { displayFormatDate } from "../../../../../../utils/helpers.js";
import { EmptyStateMessage } from "../../../../shared/empty-state-message/empty-state-message.tsx";
import {
  Badge,
  Button,
  ResponsiveDataTable,
  Select,
  type ResponsiveColumn,
} from "../../../../ui/index.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import { DEFAULT_PAGE_SIZE } from "../../constants.ts";
import { useDepartments } from "../../hooks/use-departments.ts";
import { useDesignations } from "../../hooks/use-designations.ts";
import { DesignationFormModal } from "../../modals/designation-form-modal/designation-form-modal.tsx";
import type { DesignationRow } from "../../types.ts";
import {
  formatStatusLabel,
  statusBadgeVariant,
} from "../../utils/format-status-badge.ts";
import styles from "./designations-tab.module.css";

type DesignationTableRow = Omit<DesignationRow, "id"> & {
  id: string;
} & Record<string, unknown>;

export interface DesignationsTabProps {
  departmentsRefreshKey?: number;
}

export function DesignationsTab({ departmentsRefreshKey = 0 }: DesignationsTabProps) {
  const {
    departments,
    loading: departmentsLoading,
    refetch: refetchDepartments,
  } = useDepartments();
  const {
    designations,
    departmentId,
    loading,
    submitting,
    error,
    selectDepartment,
    createDesignation,
    updateDesignation,
  } = useDesignations();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDesignation, setSelectedDesignation] =
    useState<DesignationRow | null>(null);

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  useEffect(() => {
    if (departmentsRefreshKey === 0) return;
    void refetchDepartments();
  }, [departmentsRefreshKey, refetchDepartments]);

  const departmentOptions = useMemo(
    () =>
      departments.map((dept) => ({
        value: String(dept.id),
        label: dept.name,
      })),
    [departments],
  );

  const paginatedDesignations = useMemo(() => {
    const start = (page - 1) * pageSize;
    return designations.slice(start, start + pageSize);
  }, [designations, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [departmentId, pageSize]);

  const tableRows: DesignationTableRow[] = useMemo(
    () =>
      paginatedDesignations.map((row) => ({
        ...row,
        id: String(row.id),
      })),
    [paginatedDesignations],
  );

  const openCreateModal = () => {
    setSelectedDesignation(null);
    setModalOpen(true);
  };

  const openEditModal = useCallback((row: DesignationTableRow) => {
    setSelectedDesignation({
      ...row,
      id: Number(row.id),
    });
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDesignation(null);
  };

  const handleSubmit = async (name: string) => {
    if (!departmentId) return "Select a department first.";
    const result = selectedDesignation
      ? await updateDesignation(selectedDesignation.id, name, departmentId)
      : await createDesignation(name, departmentId);

    if (result.ok) {
      if (result.message) showSuccess(result.message);
      return true;
    }
    if (result.message) showError(result.message);
    return false;
  };

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next);
    setPage(1);
  }, []);

  const columns = useMemo<ResponsiveColumn<DesignationTableRow>[]>(
    () => [
      {
        id: "name",
        header: "Designation",
        accessor: "name",
        sortable: true,
        truncate: true,
      },
      {
        id: "creationDate",
        header: "Creation Date",
        sortable: true,
        nowrap: true,
        getSortValue: (row) => row.creationDate ?? "",
        render: (row) => displayFormatDate(row.creationDate ?? ""),
        getTitleText: (row) => displayFormatDate(row.creationDate ?? ""),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        nowrap: true,
        accessor: "status",
        render: (row) => (
          <Badge variant={statusBadgeVariant(row.status)}>
            {formatStatusLabel(row.status)}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={`Edit ${row.name}`}
            onClick={() => openEditModal(row)}
          >
            <Pencil size={16} aria-hidden />
          </button>
        ),
      },
    ],
    [openEditModal],
  );

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.departmentSelect}>
          <Select
            id="designations-department"
            label="Department"
            searchable={true}
            placeholder="Select Department"
            options={departmentOptions}
            value={departmentId ?? ""}
            onChange={(value) => selectDepartment(value || null)}
            disabled={departmentsLoading || departmentOptions.length === 0}
          />
        </div>
        {departmentId ? (
          <div className={styles.actions}>
            <Button variant="accent" onClick={openCreateModal}>
              + Add Designation
            </Button>
          </div>
        ) : null}
      </div>

      {!departmentId ? (
        <EmptyStateMessage message="Select a department to view designations." />
      ) : loading ? (
        <div className={styles.loadingWrap}>
          <ThreeDots
            height="60"
            width="60"
            color="var(--color-accent-500)"
            visible
          />
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <ResponsiveDataTable<DesignationTableRow>
            serialNumber
            columns={columns}
            data={tableRows}
            defaultSortKey="name"
            defaultSortDir="asc"
            aria-label="Designations"
            emptyState={
              <EmptyStateMessage message="No designations found for this department." />
            }
            pagination
            page={page}
            pageSize={pageSize}
            totalItems={designations.length}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 30, 40]}
          />
        </div>
      )}

      <DesignationFormModal
        open={modalOpen}
        designation={selectedDesignation}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
